import { Client, ClientOptions } from '@microsoft/microsoft-graph-client';
import { useVariable } from '@softwareimaging/backstage';
import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react';
import { useRequestToken } from '../authentication/hooks/auth';
import { Variables } from '../backstage/config';

interface GraphInformation {
  client: Client;
}
const GraphContext = createContext<GraphInformation | null>(null);

export const GraphProvider: FC<PropsWithChildren> = ({ children }) => {
  const graphBaseUrl = useVariable<Variables>('graphBaseUrl');
  if (!graphBaseUrl) throw new Error('graphBaseUrl is not defined');
  const url = new URL(graphBaseUrl);
  const getAccessToken = useRequestToken();

  const client = useMemo(() => {
    const options: ClientOptions = {
      authProvider: {
        getAccessToken: async () => {
          const token = await getAccessToken();
          return token.accessToken;
        }
      },
      defaultVersion: 'v1.0',
      baseUrl: url.toString(),
      customHosts: new Set([url.hostname])
    };

    const client = Client.initWithMiddleware(options);
    return client;
  }, []);

  return <GraphContext.Provider value={{ client }}>{children}</GraphContext.Provider>;
};

export const useGraph = () => {
  const graphInformation = useContext(GraphContext);
  if (!graphInformation) {
    throw new Error('useGraph must be used within a GraphProvider');
  }
  return graphInformation;
};

export interface GraphResponse<T> {
  '@odata.context': string;
  value: T;
}
